
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { TicketInterface, TicketReviewInterface } from '@/services/QC-V2/ticket/interfaces';
import TicketReplies from '@/components/qa/ticket/TicketReplies.vue';
import TicketDetails from '@/components/qa/ticket/TicketDetails.vue';
import TicketReviewsDetails from '@/components/qa/ticket/TicketReviewsDetails.vue';
import TicketPrivateNotes from '@/components/qa/ticket/TicketPrivateNotes.vue';
import {
  EscalationRepositoryInterface,
  EscalationStatus,
  TicketEscalationInterface,
} from '@/services/QC-V2/escalation/interfaces';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import { ScoreTypeInterface, ScoreTypeRepositoryInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface, ScoreGradeRepositoryInterface } from '@/services/QC-V2/score-grade/interfaces';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { UserRepoInterface } from '@/services/Users/Interfaces';
import EscalationDetails from '@/components/qa/escalation/EscalationDetails.vue';
import TicketEscalationReview from '@/components/qa/escalation/TicketEscalationReview.vue';
import EscalationReason from '@/components/qa/escalation/EscalationReason.vue';

interface UserSelectInterface {
  id: string;
  name: string;
}

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    EscalationReason,
    EscalationDetails,
    TicketReplies,
    TicketDetails,
    TicketReviewsDetails,
    TicketPrivateNotes,
    TicketEscalationReview,
  },
  inject: [
    'escalationRepositoryV2',
    'profileRepository',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
    'userRepository',
  ],
})
export default class TicketEscalation extends Vue {
  private escalationRepositoryV2!: EscalationRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private scoreTypeRepositoryV2!: ScoreTypeRepositoryInterface;
  private scoreGradeRepositoryV2!: ScoreGradeRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;

  private escalation!: TicketEscalationInterface;
  private ticket!: TicketInterface;
  private profilesMap: ProfileMapInterface = {};
  private profiles: UserSelectInterface[] = [];

  private scoreTypes: ScoreTypeInterface[] = [];
  private scoreGrades: ScoreGradeInterface[];
  private tags: TagInterface[] = [];

  private loading: boolean = true;

  private get windowHeight(): number {
    return window.screen.height - 300;
  }

  private async created(): Promise<void> {
    if (!this.$route.params.id) {
      this.$errorDialog.show('Escalation id not provided!');
      return;
    }
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      const escalation = await this.escalationRepositoryV2.findTicketEscalationById(Number(this.$route.params.id));
      await this.fetchProfiles(escalation);
      await this.fetchScores();
      await this.fetchTags();
      this.escalation = escalation;
      this.ticket = escalation.ticket;
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async fetchScores(): Promise<void> {
    this.scoreTypes = await this.scoreTypeRepositoryV2.getAll();
    this.scoreGrades = await this.scoreGradeRepositoryV2.getAll();
  }

  private async fetchTags(): Promise<void> {
    this.tags = await this.tagRepositoryV2.getActiveTags();
  }

  private async fetchProfiles(escalation: TicketEscalationInterface): Promise<void> {
    const ids: string[] = [];
    if (!ids.includes(escalation.escalatedFrom)) {
      ids.push(escalation.escalatedFrom);
    }

    if (!!escalation.departmentManager && !ids.includes(escalation.departmentManager)) {
      ids.push(escalation.departmentManager);
    }

    if (!!escalation.shiftManager && !ids.includes(escalation.shiftManager)) {
      ids.push(escalation.shiftManager);
    }

    escalation.ticket.comments.forEach((comment) => {
      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    escalation.ticket.notes.forEach((note) => {
      if (!ids.includes(note.author)) {
        ids.push(note.author);
      }
    });

    escalation.ticket.replies.forEach((reply) => {
      if (!reply.comments.length) {
        return;
      }

      reply.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    escalation.ticket.reviews.forEach((review) => {
      if (!ids.includes(review.reviewedId)) {
        ids.push(review.reviewedId);
      }

      if (!ids.includes(review.reviewerId)) {
        ids.push(review.reviewerId);
      }

      review.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    const idsForReview = await this.extractUserIdsForReview(escalation.ticket);

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds([...ids, ...idsForReview]);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });

    this.profiles.push({
      id: 'ticket',
      name: 'Ticket Overall',
    });

    const profilesForReview = profiles.filter((profile) => idsForReview.includes(profile.userId))
      .map((profile) => ({
        id: profile.userId,
        name: `${profile.firstName} ${profile.lastName}`,
      }));

    this.profiles.push(...profilesForReview);
  }

  private async reviewEscalation(escalation: TicketEscalationInterface): Promise<void> {
    this.escalation = escalation;
    this.ticket = escalation.ticket;
    if (this.escalation.status === EscalationStatus.REVIEWED) {
      return;
    }
    this.escalation.status = EscalationStatus.REVIEWED;
    this.$forceUpdate();
  }

  private async extractUserIdsForReview(ticket: TicketInterface): Promise<string[]> {
    const ids: string[] = [];
    const emails = ticket.agents.map((agent) => agent.email);

    const users = await this.userRepository.getUsersByEmails(emails);

    users.forEach((user) => {
      if (!ids.includes(user.id)) {
        ids.push(user.id);
      }
    });

    return ids;
  }

  private handleFeedbackCreate(reviews: TicketReviewInterface[]): void {
    this.escalation.ticket.reviews = reviews;
    this.ticket.reviews = reviews;
    this.$forceUpdate();
  }
}
